// User Feed Positions Panel - Enhanced with WebSocket streaming and TP/SL matching
class PositionsPanel {
    constructor(container) {
        this.container = container;
        this.positions = [];
        this.orders = [];
        this.openOrders = [];
        this.orderGroups = new Map();
        this.groupedOrders = [];
        this.activeFilter = 'all';
        // Removed GlobalPositionsService - will be replaced with direct WebSocket
        
        this.init();
    }

    async init() {
        // console.log('🚀 Initializing PositionsPanel with WebSocket streaming...');
        
        this.render();
        this.attachEventListeners();
        this.renderPositions();
    }

    // Removed subscribe method - will be replaced with WebSocket

    // Removed loadTrackedOrders method - will be replaced with WebSocket

    /**
     * Calculate order age from timestamp
     */
    calculateOrderAge(timestamp) {
        const now = Date.now();
        const orderTime = new Date(timestamp).getTime();
        const ageMs = now - orderTime;
        
        const hours = Math.floor(ageMs / (60 * 60 * 1000));
        const minutes = Math.floor((ageMs % (60 * 60 * 1000)) / (60 * 1000));
        
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    }

    /**
     * Render the positions panel
     */
    render() {
        this.container.innerHTML = `
            <div class="positions-panel">
                <div class="panel-header">
                    <h3>Positions & Orders</h3>
                    <div class="filter-controls">
                        <button class="filter-btn active" data-filter="all">All</button>
                        <button class="filter-btn" data-filter="positions">Positions</button>
                        <button class="filter-btn" data-filter="orders">Orders</button>
                    </div>
                </div>
                <div class="positions-content">
                    <div class="positions-table-container">
                        <table class="positions-table">
                            <thead>
                                <tr id="positions-header">
                                    <!-- Headers will be populated by renderPositions -->
                                </tr>
                            </thead>
                            <tbody id="positions-body">
                                <!-- Rows will be populated by renderPositions -->
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
        // Filter button listeners
        this.container.querySelectorAll('.filter-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const filter = e.target.dataset.filter;
                this.setActiveFilter(filter);
            });
        });
    }

    /**
     * Render positions and orders table
     */
    renderPositions() {
        const headerRow = this.container.querySelector('#positions-header');
        const tbody = this.container.querySelector('#positions-body');
        
        if (!headerRow || !tbody) return;

        // Set table headers
        const headers = this.getTableHeaders();
        headerRow.innerHTML = headers.map(header => `<th>${header}</th>`).join('');

        // Get filtered data
        const filteredData = this.getFilteredData();
        
        if (filteredData.length === 0) {
            tbody.innerHTML = `
                <tr>
                    <td colspan="${headers.length}" class="no-data">
                        No ${this.activeFilter === 'all' ? 'positions or orders' : this.activeFilter} found
                    </td>
                </tr>
            `;
            return;
        }

        // Render rows
        tbody.innerHTML = filteredData.map(item => {
            if (item.type === 'position' || item.status === 'ACTIVE') {
                return this.renderPositionRow(item);
            } else {
                return this.renderOrderRow(item);
            }
        }).join('');
    }

    /**
     * Get table headers based on active filter
     */
    getTableHeaders() {
        return ['Asset', 'Size', 'Entry', 'TP', 'SL', 'PnL', 'Status', 'Age', 'ID'];
    }

    /**
     * Render position row with TP/SL inline display
     */
    renderPositionRow(position) {
        const pnlClass = position.pnl >= 0 ? 'profit' : 'loss';
        const directionClass = position.direction.toLowerCase();
        
        return `
            <tr class="position-row" data-asset="${position.asset}">
                <td>
                    <div class="asset-info">
                        <span class="asset-name">${position.asset}</span>
                        <span class="direction-badge ${directionClass}">${position.direction}</span>
                    </div>
                </td>
                <td class="size-cell">${position.size}</td>
                <td class="price-cell">$${position.entryPrice?.toFixed(4) || 'N/A'}</td>
                <td class="tp-price-cell">${position.tpPrice ? '$' + position.tpPrice.toFixed(4) : '-'}</td>
                <td class="sl-price-cell">${position.slPrice ? '$' + position.slPrice.toFixed(4) : '-'}</td>
                <td class="pnl-cell ${pnlClass}">$${position.pnl?.toFixed(2) || '0.00'}</td>
                <td class="status-cell">
                    <span class="status-badge filled">[FILLED]</span>
                </td>
                <td class="age-cell">-</td>
                <td class="cloid-cell">-</td>
            </tr>
        `;
    }

    /**
     * Render order row with TP/SL inline display
     */
    renderOrderRow(order) {
        const asset = order.coin || '';
        const isSpot = asset.startsWith('@');
        const directionClass = order.side === 'A' ? 'long' : 'short';
        // Spot pairs use BUY/SELL, perps use LONG/SHORT
        const direction = isSpot 
            ? (order.side === 'A' ? 'SELL' : 'BUY')
            : (order.side === 'A' ? 'LONG' : 'SHORT');
        const size = parseFloat(order.sz || order.size);
        const price = parseFloat(order.limitPx);
        
        return `
            <tr class="order-row" data-asset="${order.coin}" data-oid="${order.oid}">
                <td>
                    <div class="asset-info">
                        <span class="asset-name">${order.coin}</span>
                        <span class="direction-badge ${directionClass}">${direction}</span>
                    </div>
                </td>
                <td class="size-cell">${size}</td>
                <td class="price-cell">$${price.toFixed(4)}</td>
                <td class="tp-price-cell">${order.tpPrice ? '$' + order.tpPrice.toFixed(4) : '-'}</td>
                <td class="sl-price-cell">${order.slPrice ? '$' + order.slPrice.toFixed(4) : '-'}</td>
                <td class="pnl-cell">-</td>
                <td class="status-cell">
                    <span class="status-badge pending">[PENDING]</span>
                </td>
                <td class="age-cell">${order.age || '-'}</td>
                <td class="cloid-cell">${order.cloid || order.oid}</td>
            </tr>
        `;
    }

    /**
     * Get filtered data based on active filter
     */
    getFilteredData() {
        switch (this.activeFilter) {
            case 'positions':
                return this.positions || [];
            case 'orders':
                return this.orders || [];
            case 'all':
            default:
                return [...(this.positions || []), ...(this.orders || [])];
        }
    }

    /**
     * Set active filter and re-render
     */
    setActiveFilter(filter) {
        // Capture current width when switching from 'all' to preserve layout
        if (this.activeFilter === 'all' && filter !== 'all') {
            const currentWidth = this.container.offsetWidth;
            this.container.style.width = currentWidth + 'px';
        }
        
        // Reset width when switching back to 'all'
        if (filter === 'all') {
            this.container.style.width = '';
        }
        
        // Update active filter button
        this.container.querySelectorAll('.filter-btn').forEach(btn => {
            btn.classList.remove('active');
            if (btn.dataset.filter === filter) {
                btn.classList.add('active');
            }
        });
        
        this.activeFilter = filter;
        this.renderPositions();
    }

    /**
     * Update positions and orders data
     */
    updateData(positions, openOrders) {
        this.positions = positions || [];
        this.openOrders = openOrders || [];
        this.renderPositions();
    }

    /**
     * Update positions and orders data (alias for compatibility)
     */
    updatePositions(positions, openOrders) {
        // console.log('📊 PositionsPanel.updatePositions called with:', { positions, openOrders });
        this.updateData(positions, openOrders);
    }

    /**
     * Cleanup when panel is destroyed
     */
    destroy() {
        // Will be updated for WebSocket cleanup
    }
}

// Export for ES6 modules
export { PositionsPanel };

// Make PositionsPanel available globally
if (typeof window !== 'undefined') {
    window.PositionsPanel = PositionsPanel;
}
